import { useEffect, useMemo, useRef, useState } from 'react';

import { useOrganizationContext } from '@apps/web/context/organization_provider';
import { getApiClient } from '@apps/web/lib/api_client';
import { scheduleAutoSwitchToast } from '@apps/web/lib/auto_switch_toast';
import { modeStore, useMode } from '@apps/web/lib/mode_store';
import { type Mode } from '@libraries/typescript/api_client';

type ResourceType =
    | 'client'
    | 'caregiver'
    | 'conversation'
    | 'survey'
    | 'shift'
    | 'voice_call'
    | 'outreach';

type UseResourceOrganizationLookupParams = {
    readonly enabled: boolean;
    readonly resourceLabel?: string;
    readonly resourceId: string | null | undefined;
    readonly resourceType: ResourceType;
};

type UseResourceOrganizationLookupResult = {
    readonly isResolvingAcrossOrganizations: boolean;
};

type StoredResourceContext = {
    readonly organizationId: string;
    readonly mode: Mode;
    readonly pathAndSearch: string;
};

const initialContextsByResourceKey = new Map<string, StoredResourceContext>();

function getCurrentPathAndSearch(): string | null {
    if (typeof window === 'undefined') {
        return null;
    }

    return `${window.location.pathname}${window.location.search}`;
}

export function resetResourceOrganizationLookupStateForTests(): void {
    initialContextsByResourceKey.clear();
}

/**
 * Resolves the `(organization_id, mode)` that owns a resource via a
 * single backend lookup and switches the active org/mode to match.
 *
 * Replaces the N-request candidate-probing pattern used by
 * `useCrossOrganizationResourceSwitch` with one server-side query.
 */
export function useResourceOrganizationLookup({
    enabled,
    resourceLabel,
    resourceId,
    resourceType,
}: UseResourceOrganizationLookupParams): UseResourceOrganizationLookupResult {
    const { organizationId, memberships, isLoading, setOrganizationId } =
        useOrganizationContext();
    const mode = useMode();
    const attemptedResourceKeysRef = useRef(new Set<string>());
    const [isResolvingAcrossOrganizations, setIsResolvingAcrossOrganizations] =
        useState(false);
    const resourceKey = resourceId ? `${resourceType}:${resourceId}` : null;
    const storedInitialContext = resourceKey
        ? (initialContextsByResourceKey.get(resourceKey) ?? null)
        : null;
    const initialContextForResource = useMemo(
        () =>
            storedInitialContext ??
            (resourceKey && organizationId && mode
                ? {
                      organizationId,
                      mode,
                      pathAndSearch: getCurrentPathAndSearch() ?? '',
                  }
                : null),
        [mode, organizationId, resourceKey, storedInitialContext],
    );
    const isInInitialContextForResource =
        !!organizationId &&
        !!mode &&
        !!initialContextForResource &&
        organizationId === initialContextForResource.organizationId &&
        mode === initialContextForResource.mode;
    const shouldResolveAcrossOrganizations =
        enabled &&
        !isLoading &&
        !!resourceKey &&
        !!organizationId &&
        !!mode &&
        isInInitialContextForResource &&
        !attemptedResourceKeysRef.current.has(resourceKey);

    useEffect(() => {
        if (!resourceKey || !initialContextForResource) {
            return;
        }

        if (!storedInitialContext) {
            initialContextsByResourceKey.set(
                resourceKey,
                initialContextForResource,
            );
        }

        return () => {
            const currentStoredInitialContext =
                initialContextsByResourceKey.get(resourceKey);
            if (!currentStoredInitialContext) {
                return;
            }

            const currentPathAndSearch = getCurrentPathAndSearch();
            if (
                currentPathAndSearch !== null &&
                currentPathAndSearch !==
                    currentStoredInitialContext.pathAndSearch
            ) {
                initialContextsByResourceKey.delete(resourceKey);
            }
        };
    }, [initialContextForResource, resourceKey, storedInitialContext]);

    useEffect(() => {
        if (
            !enabled ||
            !resourceId ||
            isLoading ||
            !organizationId ||
            !mode ||
            !isInInitialContextForResource ||
            !resourceKey ||
            attemptedResourceKeysRef.current.has(resourceKey)
        ) {
            setIsResolvingAcrossOrganizations(false);
            return;
        }

        const attemptedResourceKeys = attemptedResourceKeysRef.current;
        attemptedResourceKeys.add(resourceKey);

        let cancelled = false;
        let completed = false;
        setIsResolvingAcrossOrganizations(true);

        void (async () => {
            try {
                const context = await getApiClient().get({
                    url: '/users/me/resource-context/{resource_type}/{resource_id}',
                    pathParams: {
                        resource_type: resourceType,
                        resource_id: resourceId,
                    },
                });

                if (cancelled) {
                    return;
                }

                const owningOrganizationId = context.organization_id;
                const owningMode = context.mode;
                const membership = memberships.find(
                    (candidate) =>
                        candidate.organization_id === owningOrganizationId,
                );

                if (owningOrganizationId !== organizationId) {
                    setOrganizationId(owningOrganizationId);
                }
                if (owningMode !== mode) {
                    modeStore.setMode(owningMode);
                }
                scheduleAutoSwitchToast({
                    organizationName:
                        owningOrganizationId !== organizationId
                            ? (membership?.organization_name ?? null)
                            : null,
                    mode: owningMode !== mode ? owningMode : null,
                    resourceLabel:
                        resourceLabel ?? resourceType.replace('_', ' '),
                });
                completed = true;
            } catch {
                if (cancelled) {
                    return;
                }
                completed = true;
            }
        })().finally(() => {
            if (!cancelled) {
                setIsResolvingAcrossOrganizations(false);
            }
        });

        return () => {
            cancelled = true;
            if (!completed) {
                attemptedResourceKeys.delete(resourceKey);
            }
        };
    }, [
        enabled,
        resourceLabel,
        resourceId,
        resourceType,
        isLoading,
        isInInitialContextForResource,
        memberships,
        mode,
        organizationId,
        resourceKey,
        setOrganizationId,
    ]);

    return {
        isResolvingAcrossOrganizations:
            isResolvingAcrossOrganizations || shouldResolveAcrossOrganizations,
    };
}
